"use client";
import React, { useEffect } from "react";
import { Breadcrumb, Button, Popconfirm, Table, Tag, message } from "antd";
import { Eye, Trash2 } from "lucide-react";
import { parseAsString, useQueryStates } from "nuqs";
import { useNavigate } from "react-router-dom";

import {
  useListSubAdminUsers,
  useSubAdminUpdate,
} from "../../../hooks/api/subAdmins/hooks";
import MyPagination from "../../../views/components/Pagination";
import { useMessageApi } from "../../../provider/AntdNotification";

interface Props {
  allowURLStateUpdate?: boolean;
}

const SubAdminsList: React.FC<Props> = ({ allowURLStateUpdate = true }) => {
  const navigate = useNavigate();
  const messageApi = useMessageApi();

  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  // --- Search binding with URL ---
  const [{ key: searchValue }, setSearchQuery] = useQueryStates(
    { key: parseAsString.withDefault("") },
    DEFAULT_NUQS_CONFIG
  );

  // --- API Hook ---
  const {
    data,
    refetchWithQuery: refetch,
    isFetching,
  } = useListSubAdminUsers({
    page: 1,
    limit: 10,
    search: searchValue || undefined,
  });

  const qData = data?.qData ?? { page: 1, limit: 10 };

  // --- Delete / Update Hook ---
  const { mutate: updateUser } = useSubAdminUpdate({
    onSuccess: () => {
      messageApi.success("User removed successfully");
      refetch(qData);
    },
    onError: (error: any) => messageApi.error(error.message),
  });

  // Debounced Search + Refetch
  useEffect(() => {
    const timeout = setTimeout(() => {
      refetch({
        page: 1,
        limit: qData.limit,
        search: searchValue || undefined,
      });
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchValue]);

  // TABLE COLUMNS
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Access Rights",
      width :  300,
      dataIndex: "rights",
      render: (rights: string[]) =>
        rights?.length ? (
          rights.map((r) => (
            <Tag key={r} color="blue" className="mr-2">
              {r}
            </Tag>
          ))
        ) : (
          <Tag color="gray">None</Tag>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: boolean) => (
        <Tag color={status ? "green" : "red"}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      dataIndex: "_id",
      render: (_id: string, row: any) => (
        <div className="flex ">
          <Button
            type="link"
            onClick={() => navigate(`/sub-admin/edit/${row._id}`)}
          >
            Edit
          </Button>

          <Popconfirm
            title="Are you sure you want to delete?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => updateUser({ _id, deletedAt: new Date() })}
          >
            <Button className="text-primary" type="text">
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <div className="flex justify-between mb-5 w-full items-center">
          <div>
            <h1 className="text-2xl font-semibold">Sub Admins</h1>
            <Breadcrumb
              separator=">"
              items={[{ title: "Dashboard" }, { title: "Sub Admins" }]}
            />
          </div>

          <div className="flex justify-end mb-3">
            <Button
              className="bg-primary text-white  "
              onClick={() => navigate(`/sub-admin/create/`)}
            >
              Add Sub Admin
            </Button>
          </div>
        </div>

        <Table
          loading={isFetching}
          columns={columns}
          dataSource={data?.data || []}
          rowKey="_id"
          bordered={false}
          scroll={{ x: "max-content" }}
          pagination={false}
          className="custom-rounded-table"
          footer={() => (
            <MyPagination
              qData={qData}
              onChange={refetch}
              total={qData.total}
              DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
            />
          )}
        />
      </div>
    </div>
  );
};

export default SubAdminsList;
